/**
 * RequestInfoPage.tsx
 *
 * The public landing page behind a marketing request-info link.
 * Route: /l/:code/:suffix?  — public, outside the authenticated tree.
 *
 * A recipient opens `/l/<code>/<suffix>` from an email or SMS, sees their first
 * name and the resource, and taps ONE button; that POST is the only thing that
 * creates a lead. Opening the page never writes: mail scanners, link previewers
 * and bots open links with no person present, so the lead is never sent
 * automatically on load, whatever a design asks for.
 *
 * States, all decided by the API's resolve call:
 *  - personalized  greeting, resource, note, the button, and "Not Sarah?", which
 *                  swaps the button for a short form that creates a separate
 *                  referral lead for whoever is holding the phone.
 *  - generic       a bare link (no suffix): the resource and the form only.
 *  - preview       a server test send: a banner and an inert button, no name.
 *  - request-sent  a lead already exists for this send: the confirmation only.
 *  - deactivated   the resource and the facility's phone, no button.
 *  - neutral       forged, unknown or unverifiable: a plain card with NO
 *                  branding, so a guessed link learns nothing about an account.
 *
 * Layout follows the design team's mock: a short photo band with the account
 * logo on it, then a column of the same width with a small caps label, a serif
 * headline and square buttons. The action sits right under the headline and
 * the resource details below it, so the button is on screen without
 * scrolling. The confirmation ends with "Explore our services", and shows the
 * photo and logo the author picked for it, else the page's. Every branded
 * state closes with the account's dark logo in a footer.
 *
 * Privacy rules that are easy to break by accident:
 *  - Description and note are author/resource TEXT. They are rendered as text
 *    and never through dangerouslySetInnerHTML.
 *  - The suffix is a bearer credential. It is only ever sent back to our own
 *    API; images, the font stylesheet, the tel: link and the services link
 *    carry no referrer, and the page loads no third-party script or pixel.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, FormEvent, ReactNode, Ref, RefObject } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { formatUsPhone, normalizeUsPhone } from '@/lib/phone';
import { cn } from '@/lib/utils';
import {
  REQUEST_INFO_DEFAULT_HERO_URL,
  REQUEST_INFO_FONT_STYLESHEET,
  confirmationLook,
  heroObjectPosition,
  logoUrlFor,
  requestInfoService,
  resolveLogoVariant,
} from './requestInfo.service';
import type {
  RequestInfoBranding,
  RequestInfoConfirmLogoVariant,
  RequestInfoDetailsDisplay,
  RequestInfoFacility,
  RequestInfoLogoVariant,
  RequestInfoResolveResponse,
  RequestInfoResource,
} from './requestInfo.service';

// ─── Constants ────────────────────────────────────────────────────────────────

/**
 * The API refuses an action token younger than two seconds (so a bulk POST has
 * to pay for a resolve first). A person can tap faster than that on a quick
 * phone, so the tap waits out the remainder behind the in-progress indicator
 * rather than failing. Measured from when the token ARRIVED, which is never
 * earlier than when it was issued, so no clock needs to agree with the server.
 */
const MIN_TOKEN_AGE_MS = 2500;

const NAME_MAX = 50;
const EMAIL_MAX = 254;

/** The mock's charcoal; an account's own button colour wins over it. */
const DEFAULT_BUTTON_COLOR = '#262626';

/** Where "Explore our services" on the confirmation goes. */
const EXPLORE_SERVICES_URL = 'https://app.next-health.com';

const SERIF = "'Cormorant Garamond', 'Cormorant', Georgia, 'Times New Roman', serif";
const SANS = "'Figtree', 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif";

const MSG_TRY_SHORTLY = 'Too many requests — please try again shortly.';
const MSG_TRY_AGAIN = 'Something went wrong — try again.';
const MSG_NETWORK = "We couldn't send your request. Check your connection and try again.";

const BUTTON_CLASSES =
  'inline-flex h-14 w-full items-center justify-center gap-2 px-10 text-base font-medium tracking-wide '
  + 'transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-500 '
  + 'focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:min-w-[18rem]';

const LINK_BUTTON_CLASSES =
  'inline-flex min-h-[44px] items-center justify-center px-1 text-base font-medium text-neutral-600 underline '
  + 'underline-offset-4 hover:text-neutral-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 '
  + 'disabled:opacity-50';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * `#abc`, `#aabbcc` or `#aabbccdd` (with or without the `#`) → `#aabbcc`;
 * anything else → null. An alpha channel is dropped: the button has to be
 * opaque for the text-contrast choice below to mean anything.
 */
function normalizeHexColor(value: string | null | undefined): string | null {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.exec(String(value ?? '').trim());
  if (!match) return null;
  const hex = match[1].length === 3
    ? match[1].split('').map((c) => c + c).join('')
    : match[1].slice(0, 6);
  return `#${hex.toLowerCase()}`;
}

/** White or near-black, whichever reads better on the brand colour. */
function readableTextColor(hex: string): string {
  const channel = (offset: number) => {
    const c = parseInt(hex.slice(offset, offset + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
  const onWhite = 1.05 / (luminance + 0.05);
  const onDark = (luminance + 0.05) / 0.05;
  return onWhite >= onDark ? '#ffffff' : '#0f172a';
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/** "a member of the Montecito Med Spa team", or "our team" with no facility. */
function teamLine(facility: RequestInfoFacility | null | undefined): string {
  const name = String(facility?.name ?? '').trim();
  return name ? `a member of the ${name} team` : 'a member of our team';
}

// ─── Visitor form ─────────────────────────────────────────────────────────────

type VisitorField = 'firstName' | 'lastName' | 'email' | 'phone';

interface VisitorValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  /** Honeypot. */
  website: string;
}

const EMPTY_VISITOR: VisitorValues = { firstName: '', lastName: '', email: '', phone: '', website: '' };

/** What to say beside a field the API (or this page) refused. Never echoes a value. */
const FIELD_MESSAGES: Record<VisitorField, string> = {
  firstName: 'Please enter your first name.',
  lastName: 'Please enter your last name.',
  email: 'Please enter a valid email address.',
  phone: 'Please enter a valid 10-digit phone number.',
};

const CONTACT_REQUIRED = 'Please enter an email address or a phone number so we can reach you.';

interface FieldErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  /** Neither email nor phone — belongs to the pair, not one field. */
  contact?: string;
}

function validateVisitor(values: VisitorValues): FieldErrors {
  const errors: FieldErrors = {};
  if (!values.firstName.trim()) errors.firstName = FIELD_MESSAGES.firstName;
  if (!values.lastName.trim()) errors.lastName = FIELD_MESSAGES.lastName;

  const email = values.email.trim();
  const phone = values.phone.trim();
  if (!email && !phone) {
    errors.contact = CONTACT_REQUIRED;
  } else {
    if (email && !isValidEmail(email)) errors.email = FIELD_MESSAGES.email;
    if (phone && !normalizeUsPhone(phone)) errors.phone = FIELD_MESSAGES.phone;
  }
  return errors;
}

function hasErrors(errors: FieldErrors): boolean {
  return Object.values(errors).some(Boolean);
}

// ─── Page ─────────────────────────────────────────────────────────────────────

type PageView =
  | { kind: 'loading' }
  | { kind: 'throttled' }
  | { kind: 'neutral' }
  | { kind: 'ready'; data: RequestInfoResolveResponse; receivedAt: number };

interface Confirmation {
  facility: RequestInfoFacility | null;
}

type SubmitTarget = 'recipient' | 'visitor';

export default function RequestInfoPage() {
  const params = useParams<{ code: string; suffix?: string }>();
  // A bare link pasted at the end of a sentence can pick up the full stop
  // ("…/l/abc1234."). A real code is base36 only, so trailing sentence
  // punctuation is never part of it. (The API strips it from a suffix itself.)
  const code = (params.code ?? '').replace(/[.,;:!?)]+$/, '');
  // `/l/<code>/` arrives with no suffix param at all; either way it is a bare link.
  const suffix = params.suffix ?? '';

  const [view, setView] = useState<PageView>({ kind: 'loading' });
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [submitting, setSubmitting] = useState<SubmitTarget | null>(null);
  const [inlineError, setInlineError] = useState<{ target: SubmitTarget; message: string } | null>(null);
  const [visitorOpen, setVisitorOpen] = useState(false);
  const [visitor, setVisitor] = useState<VisitorValues>(EMPTY_VISITOR);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const requestRef = useRef(0);
  // State alone cannot stop two taps landing in the same tick; this can.
  const inFlightRef = useRef(false);
  const confirmationHeadingRef = useRef<HTMLHeadingElement>(null);
  const firstNameRef = useRef<HTMLInputElement>(null);
  const focusFirstNameRef = useRef(false);

  const load = useCallback(async () => {
    const requestId = ++requestRef.current;
    setView({ kind: 'loading' });
    const result = await requestInfoService.resolve(code, suffix);
    if (requestId !== requestRef.current) return;
    if (!result.ok) {
      setView(result.reason === 'throttled' ? { kind: 'throttled' } : { kind: 'neutral' });
      return;
    }
    setView(
      result.data.state === 'neutral'
        ? { kind: 'neutral' }
        : { kind: 'ready', data: result.data, receivedAt: Date.now() },
    );
  }, [code, suffix]);

  useEffect(() => {
    void load();
    return () => {
      // Drop any response that lands after the route changed or unmounted.
      requestRef.current++;
    };
  }, [load]);

  useEffect(() => {
    const previous = document.title;
    document.title = 'Request more information';
    return () => {
      document.title = previous;
    };
  }, []);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = REQUEST_INFO_FONT_STYLESHEET;
    link.referrerPolicy = 'no-referrer';
    document.head.appendChild(link);
    return () => {
      link.remove();
    };
  }, []);

  // Move focus to the form only when a person opened it, not on a generic page load.
  useEffect(() => {
    if (visitorOpen && focusFirstNameRef.current) {
      focusFirstNameRef.current = false;
      firstNameRef.current?.focus();
    }
  }, [visitorOpen]);

  useEffect(() => {
    if (confirmation) confirmationHeadingRef.current?.focus();
  }, [confirmation]);

  const submit = useCallback(
    async (target: SubmitTarget, fields?: VisitorValues) => {
      if (view.kind !== 'ready' || submitting || inFlightRef.current) return;
      const { data, receivedAt } = view;
      const token = data.actionToken;
      setInlineError(null);
      if (!token) {
        setInlineError({ target, message: MSG_TRY_AGAIN });
        return;
      }

      inFlightRef.current = true;
      setSubmitting(target);
      try {
        const wait = receivedAt + MIN_TOKEN_AGE_MS - Date.now();
        if (wait > 0) await sleep(wait);

        const result = await requestInfoService.request(code, {
          suffix,
          actionToken: token,
          ...(fields
            ? {
                firstName: fields.firstName.trim(),
                lastName: fields.lastName.trim(),
                email: fields.email.trim(),
                phone: fields.phone.trim(),
                website: fields.website,
              }
            : {}),
        });

        if (result.ok && result.data.state === 'request-sent') {
          setConfirmation({ facility: result.data.facility ?? data.facility ?? null });
          return;
        }

        if (result.ok) {
          // A neutral answer to a POST after a good resolve almost always means the
          // action token aged out (the tab sat open). Re-resolve ONCE for a fresh
          // token rather than telling a real recipient their link is dead.
          const again = await requestInfoService.resolve(code, suffix);
          if (!again.ok) {
            setInlineError({ target, message: again.reason === 'throttled' ? MSG_TRY_SHORTLY : MSG_TRY_AGAIN });
            return;
          }
          if (again.data.state === 'neutral') {
            setView({ kind: 'neutral' });
            return;
          }
          setView({ kind: 'ready', data: again.data, receivedAt: Date.now() });
          if (again.data.state === 'personalized' || again.data.state === 'generic') {
            setInlineError({ target, message: MSG_TRY_AGAIN });
          }
          return;
        }

        switch (result.reason) {
          case 'validation': {
            const next: FieldErrors = {};
            for (const field of result.fields) {
              if (field in FIELD_MESSAGES) next[field as VisitorField] = FIELD_MESSAGES[field as VisitorField];
            }
            if (target === 'visitor' && hasErrors(next)) {
              setFieldErrors(next);
            } else {
              setInlineError({ target, message: result.message || MSG_TRY_AGAIN });
            }
            return;
          }
          case 'throttled':
            setInlineError({ target, message: MSG_TRY_SHORTLY });
            return;
          default:
            setInlineError({ target, message: MSG_NETWORK });
        }
      } finally {
        inFlightRef.current = false;
        setSubmitting(null);
      }
    },
    [view, submitting, code, suffix],
  );

  function handleVisitorSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    const errors = validateVisitor(visitor);
    setFieldErrors(errors);
    if (hasErrors(errors)) {
      setInlineError(null);
      return;
    }
    void submit('visitor', visitor);
  }

  function updateVisitor(field: keyof VisitorValues, value: string) {
    setVisitor((v) => ({ ...v, [field]: value }));
    if (field === 'email' || field === 'phone') {
      setFieldErrors((errs) => ({ ...errs, [field]: undefined, contact: undefined }));
    } else if (field !== 'website') {
      setFieldErrors((errs) => ({ ...errs, [field]: undefined }));
    }
  }

  // ── Plain states (no branding) ─────────────────────────────────────────────

  if (view.kind === 'loading') {
    return (
      <PlainShell>
        <div className="flex flex-col items-center gap-3 py-10 text-center" role="status" aria-live="polite">
          <Loader2 className="h-8 w-8 animate-spin text-neutral-400" aria-hidden="true" />
          <p className="text-sm text-neutral-500">Loading…</p>
        </div>
      </PlainShell>
    );
  }

  if (view.kind === 'throttled') {
    return (
      <PlainShell>
        <div className="py-6 text-center">
          <h1 className="text-lg font-semibold text-neutral-800">Please wait a moment</h1>
          <p className="mt-2 text-sm text-neutral-600">{MSG_TRY_SHORTLY}</p>
          <button
            type="button"
            onClick={() => void load()}
            className="mt-5 inline-flex min-h-[44px] items-center justify-center border border-neutral-300 bg-white px-5 text-sm font-semibold text-neutral-700 hover:bg-neutral-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400"
          >
            Try again
          </button>
        </div>
      </PlainShell>
    );
  }

  if (view.kind === 'neutral') {
    return (
      <PlainShell>
        <div className="py-6 text-center">
          <h1 className="text-lg font-semibold text-neutral-800">This link is not available</h1>
          <p className="mt-2 text-sm text-neutral-500">
            Check that the link is complete, or contact the practice that sent it.
          </p>
        </div>
      </PlainShell>
    );
  }

  // ── Branded states ─────────────────────────────────────────────────────────

  const { data } = view;
  const resource = data.resource;
  const facility = data.facility ?? null;
  const firstName = String(data.firstName ?? '').trim();
  const buttonStyle = brandButtonStyle(data.branding);

  const showConfirmation = confirmation !== null || data.state === 'request-sent';
  const confirmationContent: Confirmation | null = confirmation
    ?? (data.state === 'request-sent' ? { facility } : null);
  // The confirmation can carry its own photo and logo; each follows the page's otherwise.
  const look = showConfirmation
    ? confirmationLook(data)
    : { heroImageUrl: data.heroImageUrl, logoVariant: data.logoVariant };

  const recipientError = inlineError?.target === 'recipient' ? inlineError.message : null;
  const visitorError = inlineError?.target === 'visitor' ? inlineError.message : null;

  const interestedHeading = <InterestedHeading resourceName={resource?.name} />;

  let body: ReactNode = null;
  if (!showConfirmation) {
    switch (data.state) {
      case 'personalized':
        body = (
          <>
            {interestedHeading}
            <PersonalizedLead firstName={firstName} facility={facility} />
            {/* The action comes before the details so it is on screen when the page opens. */}
            {visitorOpen ? (
              <VisitorForm
                heading="Request info for yourself"
                intro={`Enter your details and ${teamLine(facility)} will reach out to you.`}
                values={visitor}
                errors={fieldErrors}
                formError={visitorError}
                submitting={submitting === 'visitor'}
                buttonStyle={buttonStyle}
                firstNameRef={firstNameRef}
                onChange={updateVisitor}
                onSubmit={handleVisitorSubmit}
                onCancel={() => {
                  setVisitorOpen(false);
                  setFieldErrors({});
                  setInlineError(null);
                }}
              />
            ) : (
              <div className="mt-8">
                {recipientError && <InlineError message={recipientError} />}
                <div className="flex flex-col items-center gap-3 sm:flex-row sm:gap-8">
                  <PrimaryButton
                    style={buttonStyle}
                    busy={submitting === 'recipient'}
                    onClick={() => void submit('recipient')}
                  >
                    Request more info
                  </PrimaryButton>
                  <button
                    type="button"
                    onClick={() => {
                      focusFirstNameRef.current = true;
                      setInlineError(null);
                      setVisitorOpen(true);
                    }}
                    disabled={submitting !== null}
                    className={LINK_BUTTON_CLASSES}
                  >
                    {firstName ? `Not ${firstName}?` : 'Not you?'}
                  </button>
                </div>
              </div>
            )}
            <ResourceDetails resource={resource} note={data.note} display={data} />
          </>
        );
        break;

      case 'generic':
        body = (
          <>
            {interestedHeading}
            <Lead>Enter your details below and {teamLine(facility)} will reach out to you shortly.</Lead>
            <VisitorForm
              heading="Your details"
              values={visitor}
              errors={fieldErrors}
              formError={visitorError}
              submitting={submitting === 'visitor'}
              buttonStyle={buttonStyle}
              firstNameRef={firstNameRef}
              onChange={updateVisitor}
              onSubmit={handleVisitorSubmit}
            />
            <ResourceDetails resource={resource} note={data.note} display={data} />
          </>
        );
        break;

      case 'preview':
        body = (
          <>
            {interestedHeading}
            <Lead>Recipients see their first name here and can request more information in one tap.</Lead>
            <div className="mt-8">
              <PrimaryButton style={buttonStyle} disabled>
                Request more info
              </PrimaryButton>
            </div>
            <ResourceDetails resource={resource} note={data.note} display={data} />
          </>
        );
        break;

      case 'deactivated':
        body = (
          <>
            {interestedHeading}
            <Lead>This link is no longer taking requests online.</Lead>
            <p className="mt-6 text-base text-neutral-600">
              <FacilityContactLine facility={facility} lead="Call" />
            </p>
            <ResourceDetails resource={resource} display={data} />
          </>
        );
        break;

      default:
        body = null;
    }
  }

  const banner = data.state === 'preview' && !showConfirmation ? (
    <p role="note" className="bg-amber-100 px-5 py-3 text-center text-sm font-semibold text-amber-900 sm:px-6">
      Preview — no lead is created from this page
    </p>
  ) : null;

  return (
    <BrandShell
      branding={data.branding}
      heroImageUrl={look.heroImageUrl}
      logoVariant={look.logoVariant}
      banner={banner}
    >
      {/* The live region stays mounted for the page's whole life, so the
          confirmation that replaces the button is announced when it arrives. */}
      <div role="status" aria-live="polite" aria-atomic="true">
        {showConfirmation && confirmationContent && (
          <ConfirmationBody
            facility={confirmationContent.facility}
            buttonStyle={buttonStyle}
            headingRef={confirmationHeadingRef}
          />
        )}
      </div>
      {body}
    </BrandShell>
  );
}

// ─── Generator preview ────────────────────────────────────────────────────────

/**
 * The page as a recipient would see it, drawn from what an author has picked so
 * far — for the live preview beside the link generator (Marketing → Templates →
 * Request-Info Links). Nothing in it is live: the buttons are inert, and it
 * makes no API call of its own.
 */
export function RequestInfoPreview({
  view,
  resource,
  note,
  branding,
  heroImageUrl,
  logoVariant,
  confirmHeroImageUrl,
  confirmLogoVariant,
  showResourceImage,
  showResourceDescription,
  customDescription,
  firstName,
  facility = null,
}: {
  view: 'request' | 'confirmation';
  resource?: RequestInfoResource;
  note?: string;
  branding?: RequestInfoBranding;
  heroImageUrl?: string | null;
  logoVariant?: RequestInfoLogoVariant;
  /** The confirmation's own photo and logo; absent = the page's. */
  confirmHeroImageUrl?: string | null;
  confirmLogoVariant?: RequestInfoConfirmLogoVariant;
  /** A sample name for the greeting. */
  firstName?: string;
  /** A sample facility, as a campaign's send carries the recipient's. */
  facility?: RequestInfoFacility | null;
} & RequestInfoDetailsDisplay) {
  const buttonStyle = brandButtonStyle(branding);
  const name = String(firstName ?? '').trim();
  const look = view === 'confirmation'
    ? confirmationLook({ heroImageUrl, logoVariant, confirmHeroImageUrl, confirmLogoVariant })
    : { heroImageUrl, logoVariant };

  return (
    <BrandShell branding={branding} heroImageUrl={look.heroImageUrl} logoVariant={look.logoVariant}>
      {view === 'confirmation' ? (
        <ConfirmationBody facility={facility} buttonStyle={buttonStyle} />
      ) : (
        <>
          <InterestedHeading resourceName={resource?.name} />
          <PersonalizedLead firstName={name} facility={facility} />
          {/* Drawn exactly as a recipient sees them (not greyed out as disabled
              would be), but inert: no handler, out of the tab order. */}
          <div className="mt-8">
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:gap-8">
              <button type="button" tabIndex={-1} aria-disabled="true" style={buttonStyle} className={BUTTON_CLASSES}>
                Request more info
              </button>
              <button type="button" tabIndex={-1} aria-disabled="true" className={LINK_BUTTON_CLASSES}>
                {name ? `Not ${name}?` : 'Not you?'}
              </button>
            </div>
          </div>
          <ResourceDetails
            resource={resource}
            note={note}
            display={{ showResourceImage, showResourceDescription, customDescription }}
          />
        </>
      )}
    </BrandShell>
  );
}

// ─── Layout pieces ────────────────────────────────────────────────────────────

/** The account's button colour (or the mock's charcoal) with readable text on it. */
function brandButtonStyle(branding: RequestInfoBranding | undefined): CSSProperties {
  const color = normalizeHexColor(branding?.buttonColor) ?? DEFAULT_BUTTON_COLOR;
  return { backgroundColor: color, color: readableTextColor(color) };
}

function InterestedHeading({ resourceName }: { resourceName?: string }) {
  return (
    <DisplayHeading>
      {resourceName ? (
        <>Interested in <em>{resourceName}</em>?</>
      ) : (
        <>Interested in learning <em>more</em>?</>
      )}
    </DisplayHeading>
  );
}

function PersonalizedLead({ firstName, facility }: { firstName: string; facility: RequestInfoFacility | null }) {
  return (
    <Lead>
      {firstName ? `Hi ${firstName}, tap` : 'Tap'} below and {teamLine(facility)} will reach
      out to you shortly with more information.
    </Lead>
  );
}

/** Used for loading, neutral and throttled: nothing account-specific at all. */
function PlainShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-neutral-50" style={{ fontFamily: SANS }}>
      <main className="mx-auto w-full max-w-lg px-4 py-10 pb-28">
        <div className="border border-neutral-200 bg-white p-6 shadow-sm">{children}</div>
      </main>
    </div>
  );
}

function BrandShell({
  branding,
  heroImageUrl,
  logoVariant,
  banner,
  children,
}: {
  branding?: RequestInfoBranding;
  /** The link's own photo; null or absent = the page's default. */
  heroImageUrl?: string | null;
  /** Which branding logo sits over the photo; absent = auto. */
  logoVariant?: RequestInfoLogoVariant;
  /** Full-width strip across the very top, above the photo. */
  banner?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="min-h-screen overflow-x-hidden bg-white text-neutral-800" style={{ fontFamily: SANS }}>
      {banner}
      <Hero branding={branding} imageUrl={heroImageUrl} logoVariant={logoVariant} />
      <main className="mx-auto w-full max-w-3xl px-6 pb-12 pt-6 sm:px-8 sm:pt-8">{children}</main>
      <BrandFooter branding={branding} />
    </div>
  );
}

/**
 * The account's dark logo under everything, on every branded state and whatever
 * logo the author put on the photo: a page asking for someone's details should
 * say plainly whose it is. Nothing is shown for an account with no dark logo.
 *
 * Its bottom padding keeps the last control clear of the site-wide cookie
 * notice, which stacks to ~175px tall on a phone.
 */
function BrandFooter({ branding }: { branding?: RequestInfoBranding }) {
  const [failed, setFailed] = useState<string | null>(null);
  const logoUrl = branding?.logoUrl && branding.logoUrl !== failed ? branding.logoUrl : null;

  return (
    <footer className="mx-auto w-full max-w-3xl px-6 pb-52 sm:px-8 sm:pb-36">
      {logoUrl && (
        <div className="flex justify-center border-t border-neutral-200 pt-8">
          {/* Decorative: the page names the practice in its text already. */}
          <img
            src={logoUrl}
            alt=""
            data-testid="request-info-footer-logo"
            referrerPolicy="no-referrer"
            className="h-10 w-auto max-w-[240px] object-contain sm:h-12"
            onError={() => setFailed(logoUrl)}
          />
        </div>
      )}
    </footer>
  );
}

/**
 * The photo band with the account logo on it. Kept short and no wider than the
 * text column, so the button is on screen without scrolling.
 *
 * The logo is the one the link's author chose — the light one (made for dark
 * backgrounds), the dark one, or none — drawn as it was uploaded. A soft shade
 * behind the top edge keeps it readable: dark behind a light logo, light behind
 * a dark one.
 *
 * The link's own photo is used when it has one; if it fails to load, the
 * default photo takes its place, and if that fails too the band stays grey.
 */
function Hero({
  branding,
  imageUrl,
  logoVariant,
}: {
  branding?: RequestInfoBranding;
  imageUrl?: string | null;
  logoVariant?: RequestInfoLogoVariant;
}) {
  const [failedPhotos, setFailedPhotos] = useState<string[]>([]);
  const [failedLogos, setFailedLogos] = useState<string[]>([]);
  const chosenLogo = logoUrlFor(logoVariant, branding);
  const logoUrl = chosenLogo && !failedLogos.includes(chosenLogo) ? chosenLogo : null;
  const lightLogo = resolveLogoVariant(logoVariant, branding) === 'light';
  const photo = [imageUrl, REQUEST_INFO_DEFAULT_HERO_URL].find(
    (url): url is string => Boolean(url) && !failedPhotos.includes(url as string),
  );

  return (
    <header className="mx-auto w-full max-w-3xl sm:px-8 sm:pt-8">
      <div className="relative h-44 max-h-[30vh] w-full overflow-hidden bg-neutral-700 sm:h-56 lg:h-64">
        {photo && (
          <img
            key={photo}
            src={photo}
            alt=""
            referrerPolicy="no-referrer"
            className="absolute inset-0 h-full w-full object-cover"
            style={{ objectPosition: heroObjectPosition(photo) }}
            onError={() => setFailedPhotos((failed) => [...failed, photo])}
          />
        )}
        {logoUrl && (
          <>
            <div
              aria-hidden="true"
              className={cn(
                'absolute inset-x-0 top-0 h-24 bg-gradient-to-b to-transparent',
                lightLogo ? 'from-black/40' : 'from-white/60',
              )}
            />
            <img
              key={logoUrl}
              src={logoUrl}
              alt="Practice logo"
              referrerPolicy="no-referrer"
              className="absolute left-6 top-5 h-7 w-auto max-w-[60%] object-contain object-left sm:left-8 sm:top-6 sm:h-8"
              onError={() => setFailedLogos((failed) => [...failed, logoUrl])}
            />
          </>
        )}
      </div>
    </header>
  );
}

/** The small spaced capitals over a hairline, e.g. "REQUEST RECEIVED". */
function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <>
      <p className="text-xs font-semibold uppercase tracking-[0.3em] text-neutral-500 sm:text-sm">{children}</p>
      <hr className="mb-5 mt-4 border-neutral-200 sm:mb-6" />
    </>
  );
}

function DisplayHeading({ children, headingRef }: { children: ReactNode; headingRef?: Ref<HTMLHeadingElement> }) {
  return (
    <h1
      ref={headingRef}
      tabIndex={headingRef ? -1 : undefined}
      className="break-words text-2xl leading-[1.15] text-neutral-900 focus:outline-none sm:text-3xl lg:text-4xl"
      style={{ fontFamily: SERIF, fontWeight: 500 }}
    >
      {children}
    </h1>
  );
}

function Lead({ children }: { children: ReactNode }) {
  return <p className="mt-4 max-w-2xl text-base leading-relaxed text-neutral-700 sm:text-lg">{children}</p>;
}

function Rule() {
  return <hr className="my-7 border-neutral-200" />;
}

function ConfirmationBody({
  facility,
  buttonStyle,
  headingRef,
}: {
  facility: RequestInfoFacility | null;
  buttonStyle: CSSProperties;
  /** Focused when the confirmation replaces the button; absent in the preview. */
  headingRef?: RefObject<HTMLHeadingElement>;
}) {
  const rawPhone = String(facility?.phone ?? '').trim();
  const clinic = String(facility?.name ?? '').trim() || 'your local clinic';

  return (
    <>
      <Eyebrow>Request received</Eyebrow>
      <DisplayHeading headingRef={headingRef}>
        Thank you for your <em>interest</em>.
      </DisplayHeading>
      <Lead>A member of our team will reach out to you shortly to book your complimentary consultation.</Lead>
      <Rule />
      <p data-testid="request-info-call-line" className="text-base leading-relaxed text-neutral-600">
        {rawPhone ? (
          <>
            Prefer to talk sooner? Call {clinic} at <PhoneLink rawPhone={rawPhone} /> and we’ll find you a time.
          </>
        ) : (
          <>Prefer to talk sooner? Call your local clinic and we’ll find you a time.</>
        )}
      </p>
      <div className="mt-8">
        <a
          href={EXPLORE_SERVICES_URL}
          rel="noreferrer"
          referrerPolicy="no-referrer"
          style={buttonStyle}
          className={BUTTON_CLASSES}
        >
          Explore our services
        </a>
      </div>
    </>
  );
}

/**
 * Image, description and note. The resource's price is deliberately never
 * shown: the page is an invitation to talk, not a price list.
 *
 * The link's author can hide the photo or the description, and can write a
 * description of their own to show in place of the resource's.
 */
function ResourceDetails({
  resource,
  note,
  display,
}: {
  resource?: RequestInfoResource;
  note?: string;
  display?: RequestInfoDetailsDisplay;
}) {
  const [imageFailed, setImageFailed] = useState(false);
  if (!resource) return null;
  const description = display?.showResourceDescription === false
    ? ''
    : (String(display?.customDescription ?? '').trim() || String(resource.description ?? '').trim());
  const noteText = String(note ?? '').trim();
  const showImage = display?.showResourceImage !== false && Boolean(resource.imageUrl) && !imageFailed;
  if (!description && !showImage && !noteText) return null;

  return (
    <div className="mt-12 border-t border-neutral-200 pt-10">
      {(showImage || description) && (
        <div className={showImage ? 'grid gap-6 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] sm:items-start sm:gap-10' : ''}>
          {showImage && (
            <img
              src={resource.imageUrl ?? undefined}
              alt={resource.name}
              referrerPolicy="no-referrer"
              className="block h-auto max-h-80 w-full object-cover"
              onError={() => setImageFailed(true)}
            />
          )}
          {description && (
            <p className="min-w-0 whitespace-pre-line break-words text-base leading-relaxed text-neutral-600">
              {description}
            </p>
          )}
        </div>
      )}
      {noteText && (
        <p
          data-testid="request-info-note"
          className="mt-8 whitespace-pre-line break-words border-l-2 border-neutral-300 pl-4 text-base leading-relaxed text-neutral-700"
        >
          {noteText}
        </p>
      )}
    </div>
  );
}

function PrimaryButton({
  children,
  style,
  onClick,
  busy = false,
  disabled = false,
  type = 'button',
}: {
  children: ReactNode;
  style: CSSProperties;
  onClick?: () => void;
  busy?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit';
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      style={style}
      className={BUTTON_CLASSES}
    >
      {busy ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Sending…
        </>
      ) : (
        children
      )}
    </button>
  );
}

function InlineError({ message }: { message: string }) {
  return (
    <p role="alert" className="mb-4 bg-red-50 px-4 py-3 text-sm text-red-700">
      {message}
    </p>
  );
}

function PhoneLink({ rawPhone }: { rawPhone: string }) {
  const digits = normalizeUsPhone(rawPhone) ?? rawPhone.replace(/[^\d+]/g, '');
  const shown = formatUsPhone(rawPhone) || rawPhone;
  return (
    <a
      href={`tel:${digits}`}
      rel="noreferrer"
      className="inline-flex min-h-[44px] items-center font-semibold text-neutral-900 underline underline-offset-4"
    >
      {shown}
    </a>
  );
}

function FacilityContactLine({ facility, lead }: { facility: RequestInfoFacility | null; lead: string }) {
  const name = String(facility?.name ?? '').trim() || 'the practice';
  const rawPhone = String(facility?.phone ?? '').trim();
  if (!rawPhone) {
    return <span className="block">Contact {name} to learn more.</span>;
  }
  return (
    <span className="block">
      {lead} {name} at <PhoneLink rawPhone={rawPhone} />
    </span>
  );
}

function VisitorForm({
  heading,
  intro,
  values,
  errors,
  formError,
  submitting,
  buttonStyle,
  firstNameRef,
  onChange,
  onSubmit,
  onCancel,
}: {
  heading: string;
  intro?: string;
  values: VisitorValues;
  errors: FieldErrors;
  formError: string | null;
  submitting: boolean;
  buttonStyle: CSSProperties;
  firstNameRef: RefObject<HTMLInputElement>;
  onChange: (field: keyof VisitorValues, value: string) => void;
  onSubmit: (e: FormEvent) => void;
  onCancel?: () => void;
}) {
  return (
    <form className="relative mt-8" noValidate onSubmit={onSubmit} aria-labelledby="request-info-form-heading">
      <h2
        id="request-info-form-heading"
        className="text-2xl text-neutral-900"
        style={{ fontFamily: SERIF, fontWeight: 500 }}
      >
        {heading}
      </h2>
      {intro && <p className="mt-2 text-base text-neutral-600">{intro}</p>}

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField
          id="request-info-first-name"
          label="First name"
          autoComplete="given-name"
          maxLength={NAME_MAX}
          value={values.firstName}
          error={errors.firstName}
          inputRef={firstNameRef}
          onChange={(v) => onChange('firstName', v)}
        />
        <TextField
          id="request-info-last-name"
          label="Last name"
          autoComplete="family-name"
          maxLength={NAME_MAX}
          value={values.lastName}
          error={errors.lastName}
          onChange={(v) => onChange('lastName', v)}
        />
        <TextField
          id="request-info-email"
          label="Email"
          type="email"
          inputMode="email"
          autoComplete="email"
          maxLength={EMAIL_MAX}
          value={values.email}
          error={errors.email}
          describedBy={errors.contact ? 'request-info-contact-error' : undefined}
          onChange={(v) => onChange('email', v)}
        />
        <TextField
          id="request-info-phone"
          label="Phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          maxLength={20}
          value={values.phone}
          error={errors.phone}
          describedBy={errors.contact ? 'request-info-contact-error' : undefined}
          onChange={(v) => onChange('phone', v)}
        />
      </div>
      <p className="mt-3 text-sm text-neutral-500">An email address or a phone number is enough.</p>
      {errors.contact && (
        <p id="request-info-contact-error" role="alert" className="mt-2 text-sm text-red-600">
          {errors.contact}
        </p>
      )}

      {/* Honeypot: off-screen and hidden from assistive tech. A person never
          sees it, so anything typed into it came from a bot. */}
      <div aria-hidden="true" className="absolute left-[-10000px] top-0 h-px w-px overflow-hidden">
        <label htmlFor="request-info-website">Website</label>
        <input
          id="request-info-website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={values.website}
          onChange={(e) => onChange('website', e.target.value)}
        />
      </div>

      <div className="mt-8">
        {formError && <InlineError message={formError} />}
        <div className="flex flex-col items-center gap-3 sm:flex-row sm:gap-8">
          <PrimaryButton type="submit" style={buttonStyle} busy={submitting}>
            Request more info
          </PrimaryButton>
          {onCancel && (
            <button type="button" onClick={onCancel} disabled={submitting} className={LINK_BUTTON_CLASSES}>
              Cancel
            </button>
          )}
        </div>
      </div>
    </form>
  );
}

function TextField({
  id,
  label,
  value,
  error,
  onChange,
  inputRef,
  describedBy,
  type = 'text',
  inputMode,
  autoComplete,
  maxLength,
}: {
  id: string;
  label: string;
  value: string;
  error?: string;
  onChange: (value: string) => void;
  inputRef?: RefObject<HTMLInputElement>;
  describedBy?: string;
  type?: 'text' | 'email' | 'tel';
  inputMode?: 'text' | 'email' | 'tel';
  autoComplete?: string;
  maxLength?: number;
}) {
  const errorId = error ? `${id}-error` : undefined;
  const describedByIds = [errorId, describedBy].filter(Boolean).join(' ') || undefined;
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-neutral-700">
        {label}
      </label>
      <input
        ref={inputRef}
        id={id}
        type={type}
        inputMode={inputMode}
        autoComplete={autoComplete}
        maxLength={maxLength}
        value={value}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedByIds}
        onChange={(e) => onChange(e.target.value)}
        className={
          'block h-12 w-full border bg-white px-4 text-base text-neutral-900 placeholder:text-neutral-400 '
          + 'focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500 '
          + (error ? 'border-red-500' : 'border-neutral-300')
        }
      />
      {error && (
        <p id={errorId} className="mt-1 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
